import React, { useState, useRef, useCallback, useEffect } from "react";
import { ConfigProvider, Modal, message, Typography } from "antd";

import { apiGetTemplates, apiGetProducts, apiSaveLayout, apiDeleteLayout } from "./utils/quotationApi";
import { ACCENT, CANVAS_BG, A4, DEFAULT_MARGIN } from "./constants/quotationConstants";
import { rehydrateElements, makeElement, ensurePdfLibs } from "./utils/quotationUtils";

import GalleryView from "./components/GalleryView";
import TemplateSidebar from "./components/TemplateSidebar";
import EditorToolbar from "./components/EditorToolbar";
import CanvasElement from "./components/CanvasElement";
import PropertiesPanel from "./components/PropertiesPanel";
import ElementContent from "./components/ElementContent";
import SnapGuides from "./components/SnapGuides";
import MarginBox from "./components/MarginBox";

const { Text } = Typography;

export default function FreeformQuotationBuilder({ agentId=1, initialProductId = null }) { 
  const [view, setView] = useState("gallery");
  const [orientation, setOrientation] = useState("portrait");
  const [zoom, setZoom] = useState(0.85);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [activeTemplateKey, setActiveTemplateKey] = useState("blank");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [productId, setProductId] = useState(initialProductId ? [initialProductId] : []);
  const [quotationName, setQuotationName] = useState("");
  const [currentLayoutId, setCurrentLayoutId] = useState(null);
  const [layoutsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingLayoutId, setDeletingLayoutId] = useState(null);
  const [guides, setGuides] = useState([]);
  const [margin, setMargin] = useState(DEFAULT_MARGIN);
  const canvasWrapRef = useRef(null);
  const exportRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    if (!agentId) return;
    setTemplatesLoading(true);
    try {
      const list = await apiGetTemplates(agentId);
      setTemplates(list);
    } catch (err) {
      // silent — gallery just shows the blank template if this fails
    } finally {
      setTemplatesLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ---- go back to gallery, always with a fresh list (new/edited/deleted templates reflected) ----
  const handleBackToGallery = useCallback(() => {
    setView("gallery");
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!agentId) return;
    (async () => {
      setProductsLoading(true);
      setProductsError("");
      try {
        const list = await apiGetProducts(agentId);
        setProducts(list);
      } catch (err) {
        setProductsError("Failed to load products.");
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [agentId]);

  const loadTemplate = (key) => {
    if (key === "blank") {
      setElements([]);
      setActiveTemplateKey("blank");
      setCurrentLayoutId(null);
      setQuotationName("");
      setProductId(initialProductId ? [initialProductId] : []);
      setOrientation("portrait");
      setMargin(DEFAULT_MARGIN);
      setSelectedId(null);
      setEditingId(null);
      setView("editor");
      return;
    }
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    setElements(rehydrateElements(tpl.elements));
    if (tpl.orientation) setOrientation(tpl.orientation);
    setMargin(tpl.margin || DEFAULT_MARGIN);
    setActiveTemplateKey(key);
    setCurrentLayoutId(null);
    setQuotationName("");
    setProductId(initialProductId ? [initialProductId] : []);
    setSelectedId(null);
    setEditingId(null);
    setView("editor");
  };

  // ---- delete a saved template from the gallery screen ----
  const handleDeleteTemplate = async (tpl) => {
    setDeletingLayoutId(tpl.layoutId);
    try {
      await apiDeleteLayout(tpl.layoutId);
      setTemplates((prev) => prev.filter((t) => t.layoutId !== tpl.layoutId));
      message.success("Template deleted");
    } catch (err) {
      message.error("Delete failed, please try again.");
    } finally {
      setDeletingLayoutId(null);
    }
  };

  const baseMmW = A4.mmW;
  const baseMmH = A4.mmH;
  const basePxW = A4.w;
  const basePxH = A4.h;

  const pageW = orientation === "portrait" ? basePxW : basePxH;
  const pageH = orientation === "portrait" ? basePxH : basePxW;
  const mmW = orientation === "portrait" ? baseMmW : baseMmH;
  const mmH = orientation === "portrait" ? baseMmH : baseMmW;

  const updateElement = useCallback((id, patch) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  }, []);

  const deleteElement = useCallback((id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);

  const reorderElement = useCallback((id, dir) => {
    setElements((prev) => {
      const el = prev.find((e) => e.id === id);
      const rest = prev.filter((e) => e.id !== id);
      return dir === "front" ? [...rest, el] : [el, ...rest];
    });
  }, []);

  const addElement = (type) => {
    const el = makeElement(type, 60, 60);
    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
  };

  const selected = elements.find((e) => e.id === selectedId) || null;

  // ---- keyboard shortcuts: Backspace/Delete removes the selected element,
  // arrow keys nudge it (hold Shift for a bigger 10px jump). Ignored while
  // typing inside text/table content, the name field, or any input/select. ----
  useEffect(() => {
    if (view !== "editor") return;

    const isTypingTarget = () => {
      const ae = document.activeElement;
      if (!ae) return false;
      if (ae.isContentEditable) return true;
      const tag = ae.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || !!ae.closest?.(".ant-select-selector");
    };

    const onKeyDown = (e) => {
      if (!selectedId || isTypingTarget()) return;

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        deleteElement(selectedId);
        return;
      }

      const nudge = e.shiftKey ? 10 : 1;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        updateElement(selectedId, { x: Math.round(selected.x - nudge) });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        updateElement(selectedId, { x: Math.round(selected.x + nudge) });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        updateElement(selectedId, { y: Math.round(selected.y - nudge) });
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        updateElement(selectedId, { y: Math.round(selected.y + nudge) });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [view, selectedId, selected, deleteElement, updateElement]);

  useEffect(() => {
    const printStyle = document.getElementById("__print_page_size");
    const css = `@page { size: ${mmW}mm ${mmH}mm; margin: 0; }`;
    if (printStyle) {
      printStyle.innerHTML = css;
    } else {
      const style = document.createElement("style");
      style.id = "__print_page_size";
      style.innerHTML = css;
      document.head.appendChild(style);
    }
  }, [mmW, mmH]);

  // ---- clicking the toolbar "Save" button: validate first, then open the confirm popup ----
  const handleSaveClick = () => {
    if (!quotationName.trim()) {
      message.error("Please enter a quotation name first.");
      return;
    }
    if (!productId || productId.length === 0) {
      message.error("Please select a product first.");
      return;
    }
    setSaveModalOpen(true);
  };

  // ---- actually save current layout to backend, called from the confirm popup ----
  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      const layoutId = await apiSaveLayout({
        layoutId: currentLayoutId,
        quotationName,
        productId,
        orientation,
        elements,
        margin,
      });
      setCurrentLayoutId(layoutId);
      message.success("Saved");
      setSaveModalOpen(false);
      fetchTemplates(); // keep gallery list in sync with the newly saved/updated template
    } catch (err) {
      message.error("Save failed, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setExporting(true);
    try {
      await ensurePdfLibs();
      // small delay so any images/fonts settle
      await new Promise((r) => setTimeout(r, 60));

      const canvas = await window.html2canvas(exportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: pageW,
        height: pageH,
        windowWidth: pageW,
        windowHeight: pageH,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({
        orientation: orientation === "portrait" ? "portrait" : "landscape",
        unit: "mm",
        format: [mmW, mmH],
      });
      pdf.addImage(imgData, "JPEG", 0, 0, mmW, mmH);
      pdf.save(`${quotationName || "sheet"}.pdf`);
    } catch (err) {
      message.error("PDF export failed. Try 'Print / Save as PDF' instead.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: ACCENT, borderRadius: 6 } }}>
      {view === "gallery" ? (
        <GalleryView
          templates={templates}
          templatesLoading={templatesLoading}
          onPickTemplate={loadTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          deletingLayoutId={deletingLayoutId}
        />
      ) : (
        <div style={{ fontFamily: "Arial, sans-serif", background: "#F4F6F9", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <style>{`
            @media print {
              .no-print { display: none !important; }
              html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
              .sheet-scroll {
                display: block !important;
                position: static !important;
                padding: 0 !important;
                margin: 0 !important;
                background: #fff !important;
                overflow: visible !important;
                height: auto !important;
              }
              .sheet-paper {
                box-shadow: none !important;
                margin: 0 !important;
                width: ${pageW}px !important;
                height: ${pageH}px !important;
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              .sheet-content { transform: none !important; }
            }
          `}</style>

          <EditorToolbar
            onBackToGallery={handleBackToGallery}
            quotationName={quotationName}
            onQuotationNameChange={setQuotationName}
            products={products}
            productsLoading={productsLoading}
            productsError={productsError}
            productId={productId}
            onProductIdChange={setProductId}
            layoutsLoading={layoutsLoading}
            onAddElement={addElement}
            onSaveClick={handleSaveClick}
            saving={saving}
            onDownloadPDF={handleDownloadPDF}
            exporting={exporting}
            margin={margin}
            onMarginChange={setMargin}
          />

          <Modal title="Save Quotation" open={saveModalOpen} onCancel={() => setSaveModalOpen(false)} onOk={handleConfirmSave} confirmLoading={saving} okText="Confirm Save" width={380}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#6B7686", marginBottom: 4 }}>Quotation Name</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "#33404F" }}>{quotationName}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#6B7686", marginBottom: 4 }}>Product{productId?.length > 1 ? "s" : ""}</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "#33404F" }}>
                {products
                  .filter((p) => productId?.includes(p.productId))
                  .map((p) => p.productName)
                  .join(", ") || "—"}
              </div>
            </div>
          </Modal>

          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            {/* <TemplateSidebar templates={templates} templatesLoading={templatesLoading} activeTemplateKey={activeTemplateKey} onPickTemplate={loadTemplate} /> */}

            <div className="sheet-scroll" style={{ flex: 1, background: CANVAS_BG, padding: 32, paddingBottom:"15vh", overflowY: "auto", overflowX: "auto", height: "100%", display: "flex", justifyContent: "center" }}>
              <div
                ref={canvasWrapRef}
                className="sheet-paper"
                onPointerDown={() => {
              
                  if (document.activeElement && document.activeElement !== document.body) {
                    document.activeElement.blur();
                  }
                  setSelectedId(null);
                  setEditingId(null);
                }}
                style={{
                  position: "relative",
                  width: pageW * zoom,
                  height: pageH * zoom,
                  background: "#fff",
                  boxShadow: "0 4px 24px rgba(20,30,50,0.18)",
                  flexShrink: 0,
                }}
              >
                <div className="sheet-content" style={{ position: "absolute", top: 0, left: 0, width: pageW, height: pageH, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                  <MarginBox margin={margin} pageW={pageW} pageH={pageH} />
                  {elements.map((el) => (
                    <CanvasElement
                      key={el.id}
                      el={el}
                      scale={zoom}
                      selected={el.id === selectedId}
                      editing={el.id === editingId}
                      onSelect={setSelectedId}
                      onChange={updateElement}
                      onDelete={deleteElement}
                      onStartEdit={(id) => {
                        setSelectedId(id);
                        setEditingId(id);
                      }}
                      onStopEdit={() => setEditingId(null)}
                      elements={elements}
                      pageW={pageW}
                      pageH={pageH}
                      margin={margin}
                      onDragGuides={setGuides}
                    />
                  ))}
                  <SnapGuides guides={guides} pageW={pageW} pageH={pageH} />
                </div>
              </div>
            </div>

            <div className="no-print" style={{ width: 260, flexShrink: 0, background: "#fff", borderLeft: "1px solid #E4E8EE", height: "100%", overflowY: "auto" , paddingBottom:"15vh"}}>
              <PropertiesPanel el={selected} onChange={updateElement} onDelete={deleteElement} onReorder={reorderElement} pageW={pageW} pageH={pageH} margin={margin} />
            </div>
          </div>

          <div
            ref={exportRef}
            style={{
              position: "fixed",
              top: 0,
              left: "-99999px",
              width: pageW,
              height: pageH,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            {elements.map((el) => (
              <div key={el.id} style={{ position: "absolute", left: el.x, top: el.y, width: el.w, height: el.h, boxSizing: "border-box" }}>
                <ElementContent el={el} />
              </div>
            ))}
          </div>
        </div>
      )}
    </ConfigProvider>
  );
}